import clone from 'lodash/clone';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { useUser } from './user';
import { useArticles } from './articles';
import { useDrops } from '@/store/drops';
import { runWithBomDataLossConfirm } from '@lib/bom-data-loss';
import { isUnchanged } from '@/store/cache-primitives';
import { signalrListen } from '@lib/signalr';
import type {
  CustomFieldIdWithValueDto,
  Gender,
  WeightDto,
  WeightPatchDto,
  ProductDto,
  ProductDtoImage,
  ProductDtoRoleAssignment,
  ProductOwningBrand,
  SwitchSizeSystemArgs,
  RecreateSizeSystemSkusArgs,
  UpdateGenderAndSizesArgs2,
  UpdateColorsColor,
  UpdateImageArg,
  UpdatePrincipalTypesArgs,
  UpdateProductDropsArgsItem,
  UpdateSuppliersSupplierArgs,
  UpdateTagsArgs,
} from '@/ApiClient.ts';

// Per-product in-flight dedup so concurrent ensure/prefetch calls and background
// revalidations share a single request.
const pendingLoads = new Map<number, Promise<{ product: ProductDto }>>();

export const useProducts = defineStore('products', {
  state: () => ({
    loaded: {} as Record<number, ProductDto>,
  }),

  getters: {
    // Snapshot persistence contract — see store/warm-boot.ts
    snapshotDto: () => 'ProductDto' as const,
  },

  actions: {
    snapshotData() {
      // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
      return toRaw(this.loaded);
    },

    seedSnapshot(data: unknown) {
      if (Object.keys(this.loaded).length === 0) this.loaded = data as Record<number, ProductDto>;
    },

    setProduct(product: ProductDto) {
      this.loaded[product.id] = product;

      return product;
    },

    async loadProduct(productId: number) {
      const pending = pendingLoads.get(productId);
      if (pending) return pending;

      const load = (async () => {
        try {
          const product = await this.$api.product_GetProduct(useUser().brandId, productId);
          // Keep the existing object when revalidation returns identical data, so watchers
          // on product sub-objects (e.g. product.seasonalities) don't refire on every visit.
          const existing = this.loaded[productId];
          if (existing && isUnchanged(existing, product)) {
            return { product: existing };
          }
          this.loaded[productId] = product;
          return { product };
        } finally {
          pendingLoads.delete(productId);
        }
      })();
      pendingLoads.set(productId, load);
      return load;
    },

    async ensureProduct(productId: number) {
      const cached = this.loaded[productId];
      if (cached) {
        // Stale-while-revalidate: hand out the cached product immediately so the page
        // renders without waiting, and refresh it in the background.
        this.loadProduct(productId).catch(() => {});
        return { product: cached };
      }
      return this.loadProduct(productId);
    },

    /**
     * Which other brand of the user's owns a product this brand doesn't have — the answer
     * behind the "switch brand?" prompt on a deep link that landed on the wrong brand.
     * Undefined when no reachable brand has it (the endpoint answers 204 for that).
     */
    async loadOwningBrand(productId: number): Promise<ProductOwningBrand | undefined> {
      const owner = await this.$api.product_GetOwningBrand(useUser().brandId, productId);
      return owner?.brandId ? owner : undefined;
    },

    // Fire-and-forget warm-up, e.g. from hover prefetch. Already-cached products are
    // left alone — revalidation happens via ensureProduct on actual navigation.
    prefetchProduct(productId: number) {
      if (this.loaded[productId]) return;
      this.loadProduct(productId).catch(() => {});
    },

    async updateProductName(productId: number, name: string) {
      const product = await this.$api.product_UpdateName(useUser().brandId, productId, { name });

      this.loaded[productId] = product;

      return { product };
    },

    async updateGenders(productId: number, genders: Gender[]) {
      const { product, articles } = await this.$api.product_UpdateGenders(useUser().brandId, productId, genders);
      useArticles().setArticlesForProduct(productId, articles);
      this.loaded[productId] = product;
    },

    async updateGenderNSize(productId: number, genderNSizes: UpdateGenderAndSizesArgs2) {
      const { product, articles, updateError } = await this.$api.product_UpdateGenderAndSizes(
        useUser().brandId,
        productId,
        genderNSizes
      );

      // Use the returned articles directly
      useArticles().setArticlesForProduct(productId, articles);

      this.loaded[productId] = product;

      return { product, articles, updateError };
    },

    async switchSizeSystem(productId: number, args: SwitchSizeSystemArgs) {
      const { product, articles } = await this.$api.product_SwitchSizeSystem(useUser().brandId, productId, args);

      useArticles().setArticlesForProduct(productId, articles);
      this.loaded[productId] = product;
    },

    sizeSystemChangeRules(productId: number) {
      return this.$api.product_GetSizeSystemChangeRules(useUser().brandId, productId);
    },

    async recreateSizeSystemSkus(productId: number, args: RecreateSizeSystemSkusArgs) {
      const { product, articles } = await this.$api.product_RecreateSizeSystemSkus(useUser().brandId, productId, args);

      useArticles().setArticlesForProduct(productId, articles);
      this.loaded[productId] = product;
    },

    async updateDescription(productId: number, description: string) {
      const { product, articleIDs } = await this.$api.product_UpdateDescription(useUser().brandId, productId, {
        description,
      });

      this.loaded[productId] = product;

      return { product, articleIds: articleIDs };
    },

    async updateProductTypeCategory(productId: number, hierarchyNodeId: number, productCategoryId: number | undefined) {
      const product = await this.$api.product_UpdateHierarchyAndProductCategory(useUser().brandId, productId, {
        hierarchyNodeId,
        productCategoryId,
      });

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product;

      return product;
    },

    async updateTags(productId: number, data: { tagsData: { tagId: number }[]; tentative: boolean }) {
      const tagIds = data.tagsData.map((t) => t.tagId);
      const product = await this.$api.product_UpdateTags(useUser().brandId, productId, { tagIds });

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product;

      return product;
    },

    async massEditTags(
      productId: number,
      args: { seasonId: number | null; articleIds: number[]; addTagIds: number[]; removeTagIds: number[] }
    ) {
      const response = await this.$api.product_MassEditTags(useUser().brandId, productId, {
        seasonId: args.seasonId ?? undefined,
        articleIds: args.articleIds,
        addTagIds: args.addTagIds,
        removeTagIds: args.removeTagIds,
      });

      if (response.product) this.loaded[productId] = response.product;
      const articlesStore = useArticles();
      for (const article of response.articles ?? []) articlesStore.updateProductArticle(article);

      return response;
    },

    async updateSeasons(
      productId: number,
      args: {
        articleIds: number[];
        addSeasonIds: number[];
        removeSeasonIds: number[];
        removeFromProductSeasonIds: number[];
      }
    ) {
      const result = await runWithBomDataLossConfirm((suppressBomDropWarning) =>
        this.$api.product_UpdateSeasons(useUser().brandId, productId, {
          ...args,
          suppressBomDropWarning,
        })
      );
      if (result === null) return null;

      const { product, updatedArticleIds } = result;

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product!;

      return { product, updatedArticleIds };
    },

    async updateOrders(productId: number, inOrders: any[] = []) {
      const inOrdersFormatted = inOrders.map((order) => {
        order = clone(order);

        // .type => .typeId
        if (order.type) {
          order.typeId = order.type?.id;
          delete order.type;
        }

        // TODO: REVIEW if really needed
        // Ensure integer (and Number type)
        order.quantity = parseInt(order.quantity);

        return order;
      });

      // const inOrders = [
      //   // Update order - pass along id
      //   {
      //     id: 1,
      //     inboundDate: new Date('2021-01-15'),
      //     typeID: null,
      //     status: 1,
      //     quantity: 200,
      //     lockedQuantity: true
      //   },
      //   // Add order - no id
      //   {
      //     inboundDate: new Date('2021-01-31'),
      //     typeID: 1,
      //     status: 0,
      //     quantity: 400,
      //     lockedQuantity: true
      //   }
      // ];

      const orders = await this.$api.product_UpdateDeliveries(useUser().brandId, productId, inOrdersFormatted);

      await useArticles().loadArticlesForProduct(productId);

      if (this.loaded[productId]) this.loaded[productId].orders = orders;

      return { orders };
    },

    async updateSuppliers(productId: number, suppliers: UpdateSuppliersSupplierArgs[], seasonId?: number) {
      const product = await this.$api.product_UpdateSuppliers(useUser().brandId, productId, seasonId, suppliers);

      await useArticles().loadArticlesForProduct(productId);
      this.loaded[productId] = product;

      return { product };
    },

    async updateColors(
      productId: number,
      colors: UpdateColorsColor[],
      { removeArticlesEvenIfNoColor } = { removeArticlesEvenIfNoColor: false }
    ) {
      // The picker has already turned each entry into a write intent.
      const { product, articles } = await this.$api.product_UpdateColors(
        useUser().brandId,
        productId,
        removeArticlesEvenIfNoColor,
        colors
      );

      useArticles().setArticlesForProduct(product.id, articles);

      this.loaded[productId] = product;

      return product;
    },
    async updateImages(productId: number, images: UpdateImageArg[]) {
      const product = await this.$api.product_UpdateImages(useUser().brandId, productId, images);

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product;

      return product;
    },

    async updateRelatedProduct(productId: number, relatedProductId: number | undefined) {
      const product = await this.$api.product_UpdateRelatedProduct(useUser().brandId, productId, relatedProductId);

      this.loaded[product.id] = product;
    },

    // Overwrites the weight on every SKU under the product. The response carries the affected
    // articles with their SKUs, so the store can be patched without refetching.
    async updateWeight(productId: number, weight: WeightPatchDto) {
      const { product, articles } = await this.$api.product_UpdateWeight(useUser().brandId, productId, weight);

      const articlesStore = useArticles();
      articles?.forEach((article) => articlesStore.updateProductArticle(article));

      this.loaded[productId] = product!;

      return { product, articles };
    },

    async updateTariffCodes(productId: number, tariffCodeIds: number[]) {
      const brandId = useUser().brandId;

      const product = await this.$api.product_UpdateTariffCodes(brandId, productId, tariffCodeIds);

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product;
      return product;
    },

    async setRoleAssignments(productId: number, assignments: ProductDtoRoleAssignment[]) {
      const brandId = useUser().brandId;
      const product = await this.$api.product_SetRoleAssignments(brandId, productId, { assignments });
      this.loaded[product.id] = product;
      return product;
    },

    async updateCustomField(productId: number, seasonId: number | null, values: CustomFieldIdWithValueDto[]) {
      const brandId = useUser().brandId;

      const product = await this.$api.product_UpdateCustomFieldValues(brandId, productId, seasonId, values);

      this.loaded[product.id] = product;
      return product;
    },

    async generateSkuCodes(productId: number, skuCodeIds: number[]) {
      await this.$api.product_GenerateSkuCodeValuesForSkus(useUser().brandId, productId, { skuCodeIds: skuCodeIds });
    },

    async updatePrincipalType(productId: number, principalTypes: UpdatePrincipalTypesArgs) {
      const { product, updatedArticleIds } = await this.$api.product_UpdatePrincipalTypes(
        useUser().brandId,
        productId,
        principalTypes
      );

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[productId] = product;

      return { product, updatedArticleIds };
    },

    async updateDrops(productId: number, dropData: UpdateProductDropsArgsItem[] = []) {
      const brandId = useUser().brandId;

      const product = await this.$api.product_UpdateDrops(brandId, productId, dropData);

      await useDrops().loadAll();
      await useArticles().loadArticlesForProduct(productId);

      this.loaded[product.id] = product;
      return product;
    },

    async updateMChart(productId: number, mChartId: number | undefined) {
      const brandId = useUser().brandId;

      const product = await this.$api.product_UpdateMChart(brandId, productId, { mChartId: mChartId });

      await useArticles().loadArticlesForProduct(productId);

      this.loaded[product.id] = product;
      return product;
    },

    listen() {
      signalrListen('ProductImagesUpdated', (productId: number, images: ProductDtoImage[]) => {
        const product = this.loaded[productId];
        if (product) product.images = images;
      });
    },
  },
});
